/**
 * NavIcon - Theme-aware navigation icons
 * Sprint 34.1: Branding & Graphics Integration
 * 
 * Custom navigation icons that switch between dark/light variants.
 */

import { useThemeStore } from '../../stores/themeStore';

// Import nav icons
import navDashboardDark from '../../assets/branding/nav-dashboard-dark.svg';
import navDashboardLight from '../../assets/branding/nav-dashboard-light.svg';
import navCertificatesDark from '../../assets/branding/nav-certificates-dark.svg';
import navCertificatesLight from '../../assets/branding/nav-certificates-light.svg';
import navOrdersDark from '../../assets/branding/nav-orders-dark.svg';
import navOrdersLight from '../../assets/branding/nav-orders-light.svg';
import navAdminDark from '../../assets/branding/nav-admin-dark.svg';
import navAdminLight from '../../assets/branding/nav-admin-light.svg';
import navSshLight from '../../assets/branding/nav-ssh-light.svg';
// Note: nav-ssh-dark.svg not yet available, fallback to light
import navValidatorDark from '../../assets/branding/nav-validator-dark.svg';
import navValidatorLight from '../../assets/branding/nav-validator-light.svg';

export type NavIconType = 'dashboard' | 'certificates' | 'orders' | 'admin' | 'ssh' | 'validator';

interface NavIconProps {
  /** Which icon to display */
  icon: NavIconType;
  /** Size of the icon (defaults to 20px) */
  size?: number;
  /** Additional CSS class names */
  className?: string;
  /** Inline style overrides */
  style?: React.CSSProperties;
}

/**
 * Get icon source based on type and theme
 */
function getIconSrc(icon: NavIconType, theme: 'light' | 'dark'): string {
  const icons: Record<NavIconType, { dark: string; light: string }> = {
    dashboard: { dark: navDashboardDark, light: navDashboardLight },
    certificates: { dark: navCertificatesDark, light: navCertificatesLight },
    orders: { dark: navOrdersDark, light: navOrdersLight },
    admin: { dark: navAdminDark, light: navAdminLight },
    ssh: { dark: navSshLight, light: navSshLight }, // SSH dark variant pending
    validator: { dark: navValidatorDark, light: navValidatorLight },
  };
  
  return theme === 'dark' ? icons[icon].dark : icons[icon].light;
}

/**
 * NavIcon - Theme-aware navigation icon
 * 
 * Usage:
 *   <NavIcon icon="dashboard" size={20} />
 */
export function NavIcon({
  icon,
  size = 20,
  className = '',
  style,
}: NavIconProps) {
  const resolved = useThemeStore((state) => state.resolved);
  const iconSrc = getIconSrc(icon, resolved);

  return (
    <img
      src={iconSrc}
      alt={`${icon} icon`}
      width={size}
      height={size}
      className={`nav-icon nav-icon-${icon} ${className}`}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        ...style,
      }}
    />
  );
}
